import { IPTConfidenceThresholds } from 'interfaces/i-preset-training';
import { BallHelper } from 'lib_ts/classes/ball.helper';
import {
  getMeanStdErr,
  meanObject,
  tCdfMeanInRange,
  tPPF,
} from 'lib_ts/classes/math.utilities';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { BallType } from 'lib_ts/enums/machine.enums';
import { BuildPriority } from 'lib_ts/enums/pitches.enums';
import {
  IBallState,
  IPitch,
  ITrajectory,
  ITrajektRefBreak,
} from 'lib_ts/interfaces/pitches';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import { IPresetTrainingSpec } from 'lib_ts/interfaces/training/i-preset-training-spec';
import { sum } from 'lodash';

const BASELINE_BREAKS_STD_IN = 2.5;
const BASELINE_SPEED_STD_MPH = 0.4;
const BASELINE_SPIN_STD_RPM = 150;
const RETURN_TRUE_MEAN_STD_ERROR = false;

interface IIterationScore {
  byDelta: number;
  byMse: number;
}

interface IIterationCriteria {
  label: string;
  target: number;
  mean: number;
  meanStdErr: number;
  normFactor: number;
}

interface IIterationDimension {
  name: string;
  // NaN value implies the target is irrelevant/unnecessary for the particular use-case
  target: number;
  samples: number[];
}

export class PresetTrainingHelper {
  static getStepSize = (config: {
    target: IPitch;
    shots: IMachineShot[];
  }): {
    stepSize: number;
    reason?: string;
  } => {
    if (config.shots.length === 0) {
      return {
        stepSize: 0,
        reason: 'No shots provided',
      };
    }
    const trajs = config.shots.map((s) => s.traj);
    const meanTraj = meanObject(trajs);

    if (!meanTraj) {
      return {
        stepSize: 0,
        reason: 'Empty mean traj. This should never happen.',
      };
    }

    const criteria: IIterationCriteria[] = [
      {
        label: 'speed',
        target: TrajHelper.getSpeedFromTraj(config.target.traj),
        mean: TrajHelper.getSpeedFromTraj(meanTraj),
        meanStdErr: getMeanStdErr(
          trajs.map((traj) => TrajHelper.getSpeedFromTraj(traj)),
          BASELINE_SPEED_STD_MPH,
          RETURN_TRUE_MEAN_STD_ERROR
        ),
        normFactor: 0.6, // mph
      },
    ];

    switch (config.target.priority) {
      case BuildPriority.Breaks: {
        const tBreaks = BallHelper.getSafePitchBreaks(config.target);

        const validBreaks = config.shots
          .map((s) => BallHelper.getSafeShotBreaks(s))
          .filter((br) => br !== undefined) as ITrajektRefBreak[];

        const mBreaks = meanObject(validBreaks);

        if (tBreaks && mBreaks) {
          criteria.push(
            {
              label: 'break X',
              target: tBreaks.xInches,
              mean: mBreaks.xInches,
              meanStdErr: getMeanStdErr(
                validBreaks.map((br) => br.xInches),
                BASELINE_BREAKS_STD_IN,
                RETURN_TRUE_MEAN_STD_ERROR
              ),
              normFactor: 3, // i.e. 0.25 ft from spec
            },
            {
              label: 'break Z',
              target: tBreaks.zInches,
              mean: mBreaks.zInches,
              meanStdErr: getMeanStdErr(
                validBreaks.map((br) => br.zInches),
                BASELINE_BREAKS_STD_IN,
                RETURN_TRUE_MEAN_STD_ERROR
              ),
              normFactor: 3, // i.e. 0.25 ft from spec
            }
          );
        }
        break;
      }

      case BuildPriority.Default:
      case BuildPriority.Spins:
      default: {
        const validBss = config.shots
          .map((s) => s.bs)
          .filter((bs) => bs !== undefined) as IBallState[];

        const meanBs = meanObject(validBss);

        if (!meanBs) {
          return {
            stepSize: 0,
            reason: 'No mean bs on spin priority pitch.',
          };
        }

        criteria.push(
          {
            label: 'spin X',
            target: config.target.bs.wx,
            mean: meanBs.wx,
            meanStdErr: getMeanStdErr(
              validBss.map((bs) => bs.wx),
              BASELINE_SPIN_STD_RPM,
              RETURN_TRUE_MEAN_STD_ERROR
            ),
            normFactor: 130,
          },
          {
            label: 'spin Y',
            target: config.target.bs.wy,
            mean: meanBs.wy,
            meanStdErr: getMeanStdErr(
              validBss.map((bs) => bs.wy),
              BASELINE_SPIN_STD_RPM,
              RETURN_TRUE_MEAN_STD_ERROR
            ),
            normFactor: 70,
          },
          {
            label: 'spin Z',
            target: config.target.bs.wz,
            mean: meanBs.wz,
            meanStdErr: getMeanStdErr(
              validBss.map((bs) => bs.wz),
              BASELINE_SPIN_STD_RPM,
              RETURN_TRUE_MEAN_STD_ERROR
            ),
            normFactor: 130,
          }
        );
        break;
      }
    }

    const errorNorms = criteria.map(
      (c) => Math.abs(c.target - c.mean) / c.normFactor
    );

    const errorMag = Math.sqrt(sum(errorNorms.map((m) => Math.pow(m, 2))));

    const STEP_CONFIDENCE_LVL = 0.7;

    const tCritical = tPPF(
      (1 + STEP_CONFIDENCE_LVL) * 0.5,
      config.shots.length - 1
    );

    if (tCritical === undefined) {
      return {
        stepSize: 0,
        reason: 'Undefined tCritical',
      };
    }

    // normalized confidence intervals
    const normCIs = criteria.map(
      (c) => (tCritical * c.meanStdErr) / c.normFactor
    );

    const normCIMag = Math.sqrt(sum(normCIs.map((m) => Math.pow(m, 2))));

    const sampleAlpha = errorMag / (errorMag + normCIMag);

    const MIN_STEP_SIZE = 0.5;

    if (sampleAlpha > MIN_STEP_SIZE) {
      return {
        stepSize: sampleAlpha,
      };
    }

    return {
      stepSize: MIN_STEP_SIZE,
      reason: `Sample alpha was too small (${sampleAlpha})`,
    };
  };

  static isEnoughConfidence = (config: {
    priority: BuildPriority;
    safeShots: IMachineShot[];
    thresholds: IPTConfidenceThresholds;
  }): {
    enough: boolean;
    reason: string;
  } => {
    console.debug({
      event: 'isEnoughConfidence',
      ...config,
    });

    if (config.safeShots.length < 2) {
      return {
        enough: false,
        reason: `Not enough shots for confidence check (${config.safeShots.length} < 2)`,
      };
    }

    if (config.safeShots.length >= 4) {
      return {
        enough: true,
        reason: `Enough shots to auto-pass confidence check (${config.safeShots.length} >= 4)`,
      };
    }

    const SAMPLE_CONFIDENCE_LVL = 0.9;

    const SPEED_CI_THRESHOLD = config.thresholds.speed; //2.6; // +/- 1.3 mph
    const BREAK_CI_THRESHOLD = config.thresholds.breaks; //12; // +/- 6 in
    const SPIN_CI_THRESHOLD = config.thresholds.spins; //300; // +/- 150 rpm

    const tCritical = tPPF(
      (1 + SAMPLE_CONFIDENCE_LVL) / 2,
      config.safeShots.length - 1
    );

    if (tCritical === undefined) {
      return {
        enough: false,
        reason: 'Undefined tCritical',
      };
    }

    const trajs = config.safeShots.map((s) => s.traj);

    const speedSem = getMeanStdErr(
      trajs.map((traj) => TrajHelper.getSpeedFromTraj(traj)),
      BASELINE_SPEED_STD_MPH,
      RETURN_TRUE_MEAN_STD_ERROR
    );

    const ci90 = speedSem * tCritical * 2;

    if (ci90 > SPEED_CI_THRESHOLD) {
      return {
        enough: false,
        reason: `Speed CI90 (${ci90.toFixed(
          2
        )}) is greater than threshold (${SPEED_CI_THRESHOLD})`,
      };
    }

    switch (config.priority) {
      case BuildPriority.Breaks: {
        // samples
        const sBreaks = config.safeShots.map(
          (s) => BallHelper.getSafeShotBreaks(s) as ITrajektRefBreak
        );

        const dims: IIterationDimension[] = [
          {
            name: 'X',
            target: NaN,
            samples: sBreaks.map((b) => b.xInches),
          },
          {
            name: 'Z',
            target: NaN,
            samples: sBreaks.map((b) => b.zInches),
          },
        ];

        for (const d of dims) {
          const sem = getMeanStdErr(
            d.samples,
            BASELINE_BREAKS_STD_IN,
            RETURN_TRUE_MEAN_STD_ERROR
          );

          const ci90 = sem * tCritical * 2;

          if (ci90 > BREAK_CI_THRESHOLD) {
            return {
              enough: false,
              reason: `Break CI90 (${ci90.toFixed(
                2
              )}) is greater than threshold (${BREAK_CI_THRESHOLD}) for break in ${
                d.name
              } dimension`,
            };
          }
        }
        break;
      }

      case BuildPriority.Default:
      case BuildPriority.Spins:
      default: {
        const validBss = config.safeShots
          .map((s) => s.bs)
          .filter((bs) => bs !== undefined) as IBallState[];

        const dims: IIterationDimension[] = [
          {
            name: 'X',
            target: NaN,
            samples: validBss.map((bs) => bs.wx),
          },
          {
            name: 'Y',
            target: NaN,
            samples: validBss.map((bs) => bs.wy),
          },
          {
            name: 'Z',
            target: NaN,
            samples: validBss.map((bs) => bs.wz),
          },
        ];

        for (const d of dims) {
          const sem = getMeanStdErr(
            d.samples,
            BASELINE_SPIN_STD_RPM,
            RETURN_TRUE_MEAN_STD_ERROR
          );

          const ci90 = sem * tCritical * 2;

          if (ci90 > SPIN_CI_THRESHOLD) {
            return {
              enough: false,
              reason: `Spin CI90 (${ci90.toFixed(
                2
              )}) is greater than threshold (${SPIN_CI_THRESHOLD}) for ${
                d.name
              } dimension`,
            };
          }
        }
        break;
      }
    }

    return {
      enough: true,
      reason: 'No failures',
    };
  };

  static isLowChanceOfAcceptable = (config: {
    target: IPitch;
    safeShots: IMachineShot[];
    ballType: BallType;
    spec: IPresetTrainingSpec;
    likelihoodLimit: number;
  }): {
    lowLikelihood: boolean;
    reason: string;
  } => {
    if (config.safeShots.length === 0) {
      return {
        lowLikelihood: false,
        reason: 'No shots provided - cannot calculate likelihood',
      };
    }
    const trajs = config.safeShots.map((s) => s.traj);
    const probabilities: number[] = [];

    const CHECK_SPEED = true;

    if (CHECK_SPEED) {
      // from doc, mph
      const threshold = config.spec.deltaSpeedMPH;

      const target = TrajHelper.getSpeedFromTraj(config.target.traj);

      const samples = trajs.map((traj) => TrajHelper.getSpeedFromTraj(traj));

      probabilities.push(
        tCdfMeanInRange(
          samples,
          BASELINE_SPEED_STD_MPH,
          target,
          threshold,
          RETURN_TRUE_MEAN_STD_ERROR
        )
      );
    }

    switch (config.target.priority) {
      case BuildPriority.Breaks: {
        // from doc, inches
        const threshold = config.spec.deltaBreaksInches;

        // targets
        const tBreak = BallHelper.getSafePitchBreaks(config.target);

        // samples
        const sBreaks = config.safeShots.map(
          (s) => BallHelper.getSafeShotBreaks(s) as ITrajektRefBreak
        );

        // mean
        const mBreak = meanObject(sBreaks);

        if (tBreak && mBreak) {
          const dims: IIterationDimension[] = [
            {
              name: 'X',
              target: tBreak.xInches,
              samples: sBreaks.map((b) => b.xInches),
            },
            {
              name: 'Z',
              target: tBreak.zInches,
              samples: sBreaks.map((b) => b.zInches),
            },
          ];

          for (const d of dims) {
            probabilities.push(
              tCdfMeanInRange(
                d.samples,
                BASELINE_BREAKS_STD_IN,
                d.target,
                threshold,
                RETURN_TRUE_MEAN_STD_ERROR
              )
            );
          }
        }
        break;
      }

      case BuildPriority.Default:
      case BuildPriority.Spins:
      default: {
        // from doc, rpm
        const validBss = config.safeShots
          .filter((s) => s.bs)
          .map((s) => s.bs as IBallState);

        if (validBss.length === 0) {
          return {
            lowLikelihood: false,
            reason:
              'No valid ball states for spin priority pitch (cant check likelihood)',
          };
        }
        const threshold = config.spec.deltaSpinsRPM;

        const dims: IIterationDimension[] = [
          {
            name: 'X',
            target: config.target.bs.wx,
            samples: validBss.map((bs) => bs.wx),
          },
          {
            name: 'Y',
            target: config.target.bs.wy,
            samples: validBss.map((bs) => bs.wy),
          },
          {
            name: 'Z',
            target: config.target.bs.wz,
            samples: validBss.map((bs) => bs.wz),
          },
        ];

        for (const d of dims) {
          probabilities.push(
            tCdfMeanInRange(
              d.samples,
              BASELINE_SPIN_STD_RPM,
              d.target,
              threshold,
              RETURN_TRUE_MEAN_STD_ERROR
            )
          );
        }
        break;
      }
    }

    // probability that every dimension's mean is in its range
    const probsAllInRange = probabilities.reduce((a, b) => a * b, 1);

    const isLow = probsAllInRange < config.likelihoodLimit;
    const reason = `Probability of acceptable: ${probsAllInRange.toFixed(
      2
    )} ( ${isLow ? '<' : '>'} ${config.likelihoodLimit})`;

    return {
      lowLikelihood: isLow,
      reason,
    };
  };

  static getIterationScore(config: {
    spec: IPresetTrainingSpec;
    target: IPitch;
    shots: IMachineShot[];
  }): IIterationScore {
    const trajs = config.shots
      .map((s) => s.traj)
      .filter((s) => s) as ITrajectory[];

    const meanTraj = MiscHelper.getMeanObject(trajs);

    interface ICriteria {
      label: string;
      target: number;

      mean: number;
      threshold: number;

      samples: number[];
      baselineStDev: number;
    }

    const criteria: ICriteria[] = [
      {
        label: 'speed',
        target: TrajHelper.getSpeedFromTraj(config.target.traj),

        mean: TrajHelper.getSpeedFromTraj(meanTraj as ITrajectory),
        threshold: config.spec.deltaSpeedMPH,

        samples: trajs
          .map((m) => TrajHelper.getSpeedFromTraj(m) as number)
          .filter((s) => s !== undefined),
        baselineStDev: BASELINE_SPEED_STD_MPH,
      },
    ];

    switch (config.target.priority) {
      case BuildPriority.Breaks: {
        const breaks = config.shots.map((s) => TrajHelper.getBreaksFromShot(s));

        const meanBreaks = MiscHelper.getMeanObject(breaks) as
          | ITrajektRefBreak
          | undefined;

        criteria.push(
          {
            label: 'break X',
            target: config.target.breaks?.xInches ?? 0,

            mean: meanBreaks?.xInches ?? Infinity,
            threshold: config.spec.deltaBreaksInches,

            samples: breaks.map((m) => m.xInches),
            baselineStDev: BASELINE_BREAKS_STD_IN,
          },
          {
            label: 'break Z',
            target: config.target.breaks?.zInches ?? 0,

            mean: meanBreaks?.zInches ?? Infinity,
            threshold: config.spec.deltaBreaksInches,

            samples: breaks.map((m) => m.zInches),
            baselineStDev: BASELINE_BREAKS_STD_IN,
          }
        );
        break;
      }

      case BuildPriority.Default:
      case BuildPriority.Spins:
      default: {
        const bss = config.shots
          .map((s) => s.bs)
          .filter((s) => s) as IBallState[];

        const meanBs = MiscHelper.getMeanObject(bss) as IBallState | undefined;

        criteria.push(
          {
            label: 'spin X',
            target: config.target.bs.wx,

            mean: meanBs?.wx ?? Infinity,
            threshold: config.spec.deltaSpinsRPM,

            samples: bss.map((m) => m.wx),
            baselineStDev: BASELINE_SPIN_STD_RPM,
          },
          {
            label: 'spin Y',
            target: config.target.bs.wy,

            mean: meanBs?.wy ?? Infinity,
            threshold: config.spec.deltaSpinsRPM,

            samples: bss.map((m) => m.wy),
            baselineStDev: BASELINE_SPIN_STD_RPM,
          },
          {
            label: 'spin Z',
            target: config.target.bs.wz,

            mean: meanBs?.wz ?? Infinity,
            threshold: config.spec.deltaSpinsRPM,

            samples: bss.map((m) => m.wz),
            baselineStDev: BASELINE_SPIN_STD_RPM,
          }
        );
        break;
      }
    }

    const scores: IIterationScore[] = criteria.map((c) => {
      const o: IIterationScore = {
        byDelta: -1,
        byMse: -1,
      };

      const delta = Math.abs(c.target - c.mean);

      if (delta === 0) {
        // unlikely result but possible with mocks
        o.byDelta = 0;
      } else if (c.threshold === 0) {
        // instead of dividing by 0
        o.byDelta = Infinity;
      } else {
        o.byDelta = delta / c.threshold;
      }

      // mean squared error
      const mse =
        c.samples
          .map((m) => Math.pow(m - c.target, 2))
          .reduce((a, b) => a + b, 0) / c.samples.length;

      if (mse === 0) {
        o.byMse = 0;
      } else if (c.threshold === 0) {
        o.byMse = Infinity;
      } else {
        o.byMse = mse / c.threshold;
      }

      return o;
    });

    const output: IIterationScore = {
      byDelta: Math.max(...scores.map((m) => m.byDelta)),
      byMse: Math.max(...scores.map((m) => m.byMse)),
    };

    return output;
  }
}
